import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Button from '../Button';
import IconHeart from '../IconHeart';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import './FavoriteButton.scss';

const FavoriteButton = ({
  height,
  isLeaderboard,
  on: onProp,
  onFavoriteClick,
  showTitle,
  shortLabel,
  variant,
  venueName,
  width,
}) => {
  const [on, setOn] = useState(onProp);
  const [ariaLiveText, setAriaLiveText] = useState({});

  useEffect(() => {
    setOn(onProp);
  }, [onProp]);

  const handleClick = (event) => {
    event.stopPropagation();
    const favorite = on ? 0 : 1;
    const ariaLiveTextObj = favorite ? {
      key: 'venue_added_to_favorites',
      values: { venueName },
      text: `${venueName} added to favorites`,
    } : {
      key: 'venue_removed_from_favorites',
      values: { venueName },
      text: `${venueName} removed from favorites`,
    };
    setAriaLiveText(ariaLiveTextObj);
    onFavoriteClick({ favorite });
  };

  const renderTitle = () => {
    if (!showTitle) return null;
    if (on) {
      return (
        <TranslationProvider translationKey="my_hit_list">
          <div className="FavoriteButton--title">My Hit List</div>
        </TranslationProvider>
      );
    }
    return (
      <TranslationProvider translationKey="add_to_hit_list">
        <div className="FavoriteButton--title">Add to My Hit List</div>
      </TranslationProvider>
    );
  };

  const getAlertAriaText = () => {
    if (!ariaLiveText.key) {
      return null;
    }
    return getTranslation({
      translationKey: ariaLiveText.key,
      translationValues: ariaLiveText.values,
      fallback: ariaLiveText.text,
    });
  };

  const getFavoritesAriaText = () => {
    if (venueName) {
      return getTranslation({
        translationKey: 'update_list_for_venue',
        translationValues: venueName,
        fallback: `Update lists for ${venueName}`,
      });
    }
    return getTranslation({
      translationKey: 'update_list_for_this_venue',
      fallback: 'Update lists for this venue',
    });
  };

  return (
    <Button
      aria-label={getFavoritesAriaText()}
      className={cx('FavoriteButton', {
        'FavoriteButton--on': on,
        'FavoriteButton--off': !on,
        'FavoriteButton--short-label': shortLabel,
      })}
      onClick={handleClick}
      type="button"
      variant={variant}
      data-testid="favourite-button"
    >
      <div role="alert" aria-live="polite" aria-label={getAlertAriaText()} />
      <IconHeart height={height} isLeaderboard={isLeaderboard} on={on} width={width} />
      {renderTitle()}
    </Button>
  );
};

FavoriteButton.propTypes = {
  height: PropTypes.string,
  isLeaderboard: PropTypes.bool,
  on: PropTypes.bool,
  onFavoriteClick: PropTypes.func,
  showTitle: PropTypes.bool,
  shortLabel: PropTypes.bool,
  variant: PropTypes.string,
  venueName: PropTypes.string,
  width: PropTypes.string,
};

FavoriteButton.defaultProps = {
  height: '20px',
  isLeaderboard: false,
  on: false,
  onFavoriteClick: () => { },
  showTitle: false,
  shortLabel: false,
  variant: 'no-style',
  venueName: undefined,
  width: '20px',
};

FavoriteButton.displayName = 'FavoriteButton';
export default FavoriteButton;
